import { useEffect, useState } from "react";
import { useBusiness } from "../../contexts/BusinessContext";
import { useNavigate } from "react-router-dom";
import { apiUrls } from "../../config/apiUrls.const";
import { Button, Spinner, Alert, Form, Card } from "react-bootstrap";
import { _get, _post } from "../../services/apiClient";
import Swal from "sweetalert2";
import { getSecureItem } from "../../utils/LocalStorageManager";

export default function EditCategorization() {
  const {business} = useBusiness();
  const [data, setData] = useState<any>(null);
  const [selectedGroups, setSelectedGroups] = useState<any>({});
  const [selectedCategories, setSelectedCategories] = useState<any>({});
  const [selectedSubcategories, setSelectedSubcategories] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const navigate = useNavigate();
  const biz_code = getSecureItem("binfo")?.[0]?.biz_code;

  useEffect(() => {
    const fetchCategorizedItems = async () => {
      setLoading(true);
      try {
        const res: any = await _get(apiUrls.categorization);
        const data = res?.data?.data || {};
        Object.keys(data).forEach((groupKey) => {
          const bizGroup = business?.services ? business.services[groupKey] : null;
          data[groupKey].forEach((category: any) => {
            const bizCategory = bizGroup && bizGroup.find((cat:any) => cat.category_id === category.id) || null;
            // set selected categories
            if (bizCategory) {
              setSelectedCategories((prev:any) => ({ ...prev, [category.id]: true }));
            }
            category?.subcategories.forEach((subcategory:any) => {
              const bizItem = bizCategory && bizCategory.subcategories.find((i:any) => i.subcategory_id === subcategory.id) || null;
              if (bizItem) {
                setSelectedSubcategories((prev:any) => ({ ...prev, [subcategory.id]: true }));
              }
            })
          });
        });
        setData(data);

        if (res?.data?.hasBusinessCategorization) {
          setSaved(true);
        }
      } catch (err) {
        setError("Failed to fetch categorized items");
      } finally {
        setLoading(false);
      }
    };

    fetchCategorizedItems();
  }, []);

  // ✅ handle group checkbox
  const handleGroupChange = (group: string, checked: boolean) => {
    setSelectedGroups((prev: any) => ({ ...prev, [group]: checked }));
    const updatedServices: any = { ...selectedCategories };
    const updatedSubcategories: any = { ...selectedSubcategories };

    if (data?.[group]) {
      data[group].forEach((category: any) => {
        updatedServices[category.id] = checked;
        category.subcategories?.forEach((subcategory: any) => {
          updatedSubcategories[subcategory.id] = checked;
        });
      });
    }

    setSelectedCategories(updatedServices);
    setSelectedSubcategories(updatedSubcategories);
  };

  // ✅ handle category checkbox
  const handleCategoryChange = (group: string, category: any, checked: boolean) => {
    setSelectedCategories((prev: any) => ({ ...prev, [category.id]: checked }));
    const updatedSubcategories: any = { ...selectedSubcategories };

    category.subcategories?.forEach((subcategory: any) => {
      updatedSubcategories[subcategory.id] = checked;
    });

    setSelectedSubcategories(updatedSubcategories);

    const allChecked =
      data[group].every((cat: any) => checked || selectedCategories[cat.id]);
    setSelectedGroups((prev: any) => ({ ...prev, [group]: allChecked }));
  };

  // ✅ handle item checkbox
  const handleSubcategoryChange = (categoryId: number, subcategoryId: number, checked: boolean) => {
    setSelectedSubcategories((prev: any) => ({ ...prev, [subcategoryId]: checked }));
  };

  // ✅ save selection
  const handleSave = async () => {
  if (!data) return;

  const selectedPayload: any[] = [];

  Object.keys(data).forEach((groupKey) => {
    // Check if group has at least one category or item selected
    const selectedCategoriesInGroup = data[groupKey]
      .filter((category: any) => selectedCategories[category.id])
      .map((category: any) => ({
        id: category.id,
        subcategories: category.subcategories
          ?.filter((subcategory: any) => selectedSubcategories[subcategory.id])
          .map((subcategory: any) => subcategory.id) || [],
      }));

    if (selectedCategoriesInGroup.length > 0) {
      selectedPayload.push({
        group: groupKey,
        categories: selectedCategoriesInGroup,
      });
    }
  });

  if (selectedPayload.length === 0) {
    Swal.fire({
      icon: "warning",
      title: "No selection made",
      text: "Please select at least one service or item!",
    });
    return;
  }

  setSaving(true);

    try {
      // First update business services
      const updateRes = await _post(
        apiUrls.updateBusinessCategorization,
        { categorization: selectedPayload, biz_code },
        // {
        //   skipGlobalSuccess: true, // We'll handle success locally for this one
        //   customErrorMessage: "Failed to update business services.",
        // }
      );

      if (updateRes.data?.success) {
        // Fetch updated service data
        const res = await _post(
          apiUrls.getBusinessCategorization,
          { biz_code },
          // { skipGlobalSuccess: true } // no need for popup here either
        );

        if (res.data?.success && res.data?.data) {
          const data = res.data.data;

          if (business) {
            business.services = data;
            setSaved(true);

            Swal.fire({
              icon: "success",
              title: "Saved successfully",
              text: "Your business service categorization has been updated.",
              timer: 2000,
              showConfirmButton: false,
              willClose: () => navigate("/categorization"),
            });
          }
        }
      }
    } catch (error) {
      console.error("Error updating business services:", error);
      // No need for manual Swal here, global interceptor already shows it
    }

  };

  // 🚀 UI rendering
  if (loading)
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );

  if (error) return <Alert variant="danger">{error}</Alert>;

  if (!saved && (!data || Object.keys(data).length === 0)) {
    return (
      <div className="text-center py-5">
        <Alert variant="info">
          You must set your business services first to continue.
        </Alert>
        <Button onClick={() => window.location.reload()} variant="primary">
          Set Categorization
        </Button>
      </div>
    );
  }

  return (
    <div className="container-fluid p-0">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Edit Categorization</h3>
        <Button className="float-end" variant="dark" size="sm" onClick={() => navigate("/categorization")}>
          Back
        </Button>
      </div>

		<div className="card">
			<div className="card-body">
				<div className="row">
					{Object.keys(data).map((groupKey) => (
					<div className="col-md-4 mb-4" key={groupKey}>
						<Card className="shadow-sm border-0 h-100">
						<Card.Header className="bg-light d-flex justify-content-between align-items-center">
							<Form.Check
							type="checkbox"
							id={`group-${groupKey}`}
							label={<strong className="text-uppercase fs-120">{groupKey}</strong>}
							checked={!!selectedGroups[groupKey]}
							onChange={(e) => handleGroupChange(groupKey, e.target.checked)}
							/>
						</Card.Header>

						<Card.Body>
							{data[groupKey].length === 0 && (
							<p className="text-muted small mb-0">No data available.</p>
							)}

							{data[groupKey].map((category: any) => (
							<div key={category.id} className="mb-3">
								<Form.Check
								type="checkbox"
								id={`cat-${category.id}`}
								label={<span className="fw-semibold">{category.name_en}</span>}
								checked={!!selectedCategories[category.id]}
								onChange={(e) =>
									handleCategoryChange(groupKey, category, e.target.checked)
								}
								/>

								<div className="ms-4 mt-1">
								{category.subcategories?.map((subcategory: any) => (
									<Form.Check
									key={subcategory.id}
									type="checkbox"
									id={`subcategory-${subcategory.id}`}
									label={<span className="">{subcategory.name_en}</span>}
									checked={!!selectedSubcategories[subcategory.id]}
									onChange={(e) =>
										handleSubcategoryChange(category.id, subcategory.id, e.target.checked)
									}
									/>
								))}
								</div>
							</div>
							))}
						</Card.Body>
						</Card>
					</div>
					))}
				</div>

				<div className="text-end mt-3">
					<Button
					variant="primary"
					disabled={saving}
					onClick={handleSave}
					className="px-4"
					>
					{saving ? "Saving..." : "Save categorization"}
					</Button>
				</div>
			</div>
		</div>
    </div>
  );
}